import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Select,
    TextField,
    MenuItem,
    FormControl,
    FormControlLabel,
    InputLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ListItemText,
    OutlinedInput,
    FormGroup
} from '@mui/material';
import PropTypes from 'prop-types';
import { Edit } from '@mui/icons-material';
import { toast } from 'react-toastify';
import ApiHelper from 'utils/apiHelper';
import EditGroupPermissionDialog from './EditGroupPermissionDialog';
import { validateAlphaPlus, validateAlphanumeric } from 'utils/commonHelpers';
import { useSelector } from 'react-redux';

const AddPermissionGroupDialog = ({ usersData, onClose, limitFields }) => {
    const [groupName, setGroupName] = useState('');
    const [description, setDescription] = useState('');
    const [rootAdmin, setRootAdmin] = useState(false);
    const [serverAdmin, setServerAdmin] = useState(false);
    const [orgAdmin, setOrgAdmin] = useState(false);
    const [groupAdmin, setGroupAdmin] = useState(false);
    const [userAdmin, setUserAdmin] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showEditGroupPermissions, setShowEditGroupPermissions] = useState(false);
    const [groupPermissionsData, setGroupPermissionsData] = useState([]);
    const { userInfo } = useSelector((state) => state.auth);

    useEffect(() => {
        getGroupPermissions();
    }, []);

    const handleChangeGroupName = (e) => {
        if (e.target.value.length > limitFields['name']) {
            toast.warning('This field must be ' + limitFields['name'] + ' characters or less.');
        } else {
            setGroupName(e.target.value);
        }
    };

    const handleChangeDesc = (e) => {
        if (e.target.value.length > limitFields['description']) {
            toast.warning('This field must be ' + limitFields['description'] + ' characters or less.');
        } else {
            setDescription(e.target.value);
        }
    };

    const getGroupPermissions = () => {
        ApiHelper.get('/api/management/group_list/')
            .then((res) => {
                setGroupPermissionsData(res.data);
            })
            .catch((err) => console.log(err));
    };

    const handleChange = (event) => {
        const {
            target: { value }
        } = event;
        setSelectedUsers(typeof value === 'string' ? value.split(',') : value);
    };

    const handleClickOrgAdmin = (e) => {
        setOrgAdmin(e.target.checked);
        setUserAdmin(true);
        setGroupAdmin(true);
    };

    const handleClickUserAdmin = (e) => {
        setUserAdmin(e.target.checked);
        setGroupAdmin(true);
    };

    const onSave = (data) => {
        setGroupPermissionsData(data);
    };

    const handleSubmit = () => {
        if (!validateAlphanumeric(groupName)) {
            toast.info('The group name must be alphanumeric');
            return true;
        }
        if (!validateAlphaPlus(description)) {
            toast.info('The group description must be alphanumeric');
            return true;
        }
        ApiHelper.post('/api/management/permission_group/', {
            group_name: groupName,
            group_description: description,
            root_admin: rootAdmin,
            server_admin: serverAdmin,
            org_admin: orgAdmin,
            group_admin: groupAdmin,
            user_admin: userAdmin,
            users: selectedUsers,
            group_permissions: groupPermissionsData
        })
            .then(() => {
                toast.success('Successfully added.');
                onClose();
            })
            .catch(() => {
                toast.error('Failed to add a new user group.');
            });
    };

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>Add New Permission Group</DialogTitle>
            <DialogContent sx={{ minWidth: '500px' }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Group Name"
                        value={groupName}
                        inputProps={{
                            maxLength: 30
                        }}
                        onChange={(e) => handleChangeGroupName(e)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{ width: '100%' }}
                        label="Group Description"
                        value={description}
                        inputProps={{
                            maxLength: 50
                        }}
                        onChange={(e) => handleChangeDesc(e)}
                    />
                </Box>
                <Box sx={{ mt: 3 }}>
                    <FormGroup aria-label="position" row>
                        {userInfo.admin_permissions.root_admin ? (
                            <>
                                <FormControlLabel
                                    value="top"
                                    control={
                                        <Checkbox
                                            disabled={!userInfo.admin_permissions.root_admin}
                                            checked={rootAdmin}
                                            onChange={(e) => setRootAdmin(e.target.checked)}
                                        />
                                    }
                                    label="Root Admin"
                                    labelPlacement="top"
                                />
                                <FormControlLabel
                                    value="top"
                                    control={
                                        <Checkbox
                                            disabled={!userInfo.admin_permissions.root_admin}
                                            checked={serverAdmin}
                                            onChange={(e) => setServerAdmin(e.target.checked)}
                                        />
                                    }
                                    label="Server Admin"
                                    labelPlacement="top"
                                />
                            </>
                        ) : (
                            ''
                        )}

                        <FormControlLabel
                            value="top"
                            control={<Checkbox checked={orgAdmin} onChange={handleClickOrgAdmin} />}
                            label="Org Admin"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox checked={userAdmin} onChange={handleClickUserAdmin} />}
                            label="User Admin"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox checked={groupAdmin} onChange={(e) => setGroupAdmin(e.target.checked)} />}
                            label="Group Admin"
                            labelPlacement="top"
                        />
                    </FormGroup>
                </Box>
                <Box>
                    <FormControl sx={{ width: '100%', mt: 2, maxWidth: '550px' }}>
                        <InputLabel>Choose Users</InputLabel>
                        <Select
                            labelId="demo-multiple-checkbox-label"
                            id="demo-multiple-checkbox"
                            multiple
                            value={selectedUsers}
                            onChange={handleChange}
                            input={<OutlinedInput label="Choose Users" />}
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {usersData &&
                                usersData.map((user, key) => (
                                    <MenuItem key={key} value={user.username}>
                                        <Checkbox checked={selectedUsers.indexOf(user.username) > -1} />
                                        <ListItemText primary={user.username} />
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Button color="primary" variant="contained" startIcon={<Edit />} onClick={() => setShowEditGroupPermissions(true)}>
                        Edit Group Permissions
                    </Button>
                    {showEditGroupPermissions && (
                        <EditGroupPermissionDialog
                            data={groupPermissionsData}
                            onSave={onSave}
                            onClose={() => setShowEditGroupPermissions(false)}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={groupName === ''}>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

AddPermissionGroupDialog.propTypes = {
    usersData: PropTypes.array,
    onClose: PropTypes.func,
    limitFields: PropTypes.object
};

export default AddPermissionGroupDialog;
